import { DownloadICS, EventsItem, ModifiedEventsItem, SlotType } from "@/types";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc.js";
import isoWeek from "dayjs/plugin/isoWeek.js";
import weekday from "dayjs/plugin/weekday.js";
import timezone from "dayjs/plugin/timezone.js";
import isBetween from "dayjs/plugin/isBetween.js";
import localeData from "dayjs/plugin/localeData.js";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import moment from "moment-timezone";

dayjs.extend(customParseFormat);

const EVENTS_BASE: string = import.meta.env.EDS_EVENT_ACCESS_BASE_URL;

export const CHANGE_EVENT_LOCAL_KEY = "change_event_data";
export const FEEDBACK_EVENT_ID = "id_event_for_feedback";
export const ACCESS_EVENT_CODE = "access_event_code";
export const ACADEMY_ACCESS_KEY = "academy";

/* extend dayjs. */
dayjs.extend(localeData);
dayjs.extend(isoWeek);
dayjs.extend(weekday);
dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const Days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const DaysShort = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const typeToDisplayText = {
  Grammar: "Grammar",
  IELTS: "IELTS session",
  Correction: "Writing Correction",
  Speaking: "Speaking Class",
  MockTest: "Mock Test",
};

export const typeToHeaderClass = {
  Grammar: "bg-[#E7DEFF]",
  IELTS: "bg-[#74DFAC]",
  Correction: "bg-[#8065C9]",
  Speaking: "bg-[#D9DAF3]",
  MockTest: "bg-[#2D224C]",
  default: "bg-[#8065C9]",
};

export const typeToTextColor = {
  Grammar: "text-[#8065C9]",
  IELTS: "text-[#2D224C]",
  Correction: "text-white",
  Speaking: "text-[#2D224C]",
  MockTest: "text-white",
  default: "text-white",
};

export const SUBTRACT_MINUTES_TO_START_ACCESS_MEET = -20;
export const ADD_MINUTES_TO_ACCESS_MEET = 20;
export const MAX_CHANGE_EVENTS_IN_A_MONTH = 3;

export const getTextColorFromType = (type: string) => {
  switch (type) {
    case "grammer":
      return "text-brand-deep";
    case "ielts":
      return "text-brand-deep";
    case "speaking":
      return "text-brand";
    case "correction":
      return "text-white";
    case "one to one":
      return "text-white";
    default:
      return "text-white";
  }
};

export const getHeaderBackgroundFromType = (type: string) => {
  switch (type) {
    case "Grammar":
      return "bg-brand-lavender";
    case "IELTS":
      return "bg-socials-whatsapp";
    case "Speaking":
      return "bg-brand-auth";
    case "MockTest":
      return "bg-edu-active";
    default:
      return "bg-brand-deep";
  }
};

export const getTextFromType = (type: string) => {
  switch (type) {
    case "Grammar":
      return "Grammar";
    case "IELTS":
      return "IELTS session";
    case "Speaking":
      return "Speaking Class";
    case "MockTest":
      return "Mock Test";
    case "Correction":
      return "Writing Correction";
    default:
      return "";
  }
};

export const getTypeClass = (
  itemType: string,
  nomeVisualizzato: string,
  isPast = false,
  isDeleted = false,
) => {
  let customStyle = "";
  switch (itemType) {
    case "speaking":
      if (nomeVisualizzato.toLowerCase() == "mock test") {
        customStyle =
          "bg-[#2D224C] text-white border-[1px] border-white lg:border-none ";
      } else if (nomeVisualizzato.toLowerCase() == "one to one") {
        customStyle = "bg-[#8065C9] text-white border-none";
      } else {
        customStyle = "bg-[#D9DAF3] text-[#2D224C] border-none";
      }
      break;
    case "grammar":
      customStyle = "bg-[#E7DEFF] text-[#8065C9] border-none";
      break;
    case "correction":
      customStyle = "bg-[#8065C9] !text-white border-none";
      break;
    case "ielts":
      customStyle = "bg-[#74DFAC] text-[#2D224C] border-none";
      break;
    case "mock test":
      customStyle = "bg-[#2D224C] !text-white border-none";
      break;
    case "one to one":
      customStyle = "bg-[#8065C9] !text-white border-none";
      break;
  }

  if (isPast) {
    customStyle = customStyle + " " + "opacity-50";
  }
  if (isDeleted) {
    customStyle = customStyle + " " + " line-through ";
  }

  return customStyle;
};
export const INTERNAL_DATE_FORMAT = "YYYY-MM-DD";
export const INTERNAL_DATE_FORMAT_WITH_TIME = "YYYY-MM-DD hh:mm";

export const getDate = (date: Dayjs) => {
  const calendar = [];
  const startDate = date.startOf("month").startOf("isoWeek");
  const endDate = date.endOf("month");
  let day = startDate.subtract(1, "day");

  while (day.isBefore(endDate, "day")) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      day = day.add(1, "day");
      week.push(day.format(INTERNAL_DATE_FORMAT));
    }
    calendar.push(week);
  }

  return calendar;
};

export const getDateTruncated = (date: moment.Moment) => {
  const calendar = [];
  // Ensure we're working with a moment in the correct timezone
  const tz = date.tz() || 'UTC';
  const startDate = date.clone().startOf("month").startOf("isoWeek");
  const endDate = date.clone().endOf("month");

  let currentDay = startDate.clone().subtract(1, "day");

  while (currentDay.isBefore(endDate, "day")) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      currentDay = currentDay.clone().add(1, "day");
      week.push(currentDay.format(INTERNAL_DATE_FORMAT));
    }
    calendar.push(week);
  }

  const d = moment().tz(tz);
  if (calendar.length > 5) {
    if (d.date() >= 26) {
      return calendar.slice(1);
    } else {
      calendar.pop();
    }
  }

  return calendar;
};

export const getMoment = (value: number) => {
  let date = dayjs();
  if (value < 0) {
    date = date.subtract(Math.abs(value), "month");
  } else {
    date = date.add(value, "month");
  }
  return date;
};

export const getMonthlyMoment = (value: number, timezo?: string) => {
  let date = timezo ? moment().tz(timezo) : moment();
  if (value < 0) {
    date = date.subtract(Math.abs(value), "month");
  } else {
    date = date.add(value, "month");
  }
  return date;
};

interface IsIntoRangeDates {
  start: Date;
  end: Date;
  date: Date;
  startMinutes: number;
  endMinutes: number;
  info?: boolean;
}

interface IReturnIsIntoRangeDates {
  status: boolean;
  start?: Date;
  end?: Date;
  date?: Date;
  startMinutes?: number;
  endMinutes?: number;
  isBefore?: boolean;
  isAfter?: boolean;
  error?: boolean;
}

export const isIntoRangeDates = (
  params?: Partial<IsIntoRangeDates>,
): IReturnIsIntoRangeDates => {
  let status = false;

  const values: IsIntoRangeDates = {
    start: new Date(),
    end: new Date(),
    date: new Date(),
    startMinutes: 0,
    endMinutes: 0,
    info: false,
    ...params,
  };

  if (!(values?.start instanceof Date))
    console.error('isIntoRangeDates({start}): "start" is not a type Date');

  if (!(values?.end instanceof Date))
    console.error('isIntoRangeDates({end}): "end" is not a type Date');

  if (!(values?.date instanceof Date))
    console.error('isIntoRangeDates({date}): "date" is not a type Date');

  if (typeof values?.startMinutes !== "number")
    console.error(
      'isIntoRangeDates({startMinutes}): "minutes" don\'t parse to int',
    );

  if (typeof values?.endMinutes !== "number")
    console.error(
      'isIntoRangeDates({endMinutes}): "minutes" don\'t parse to int',
    );

  if (
    !(values?.start instanceof Date) ||
    !(values?.end instanceof Date) ||
    !(values?.date instanceof Date) ||
    typeof values?.startMinutes !== "number" ||
    typeof values?.endMinutes !== "number"
  )
    return { status, error: true };

  const start = new Date(
    values?.start.setMinutes(values?.start.getMinutes() + values?.startMinutes),
  );
  const end = new Date(
    values?.end.setMinutes(values?.end.getMinutes() - values?.endMinutes),
  );

  status = start <= values?.date && values?.date <= end;

  const final = {
    isBefore: values?.date < start,
    isAfter: end < values?.date,
  };

  if (values.info)
    return {
      status,
      start,
      end,
      date: values.date,
      startMinutes: values.startMinutes,
      endMinutes: values.endMinutes,
      ...final,
    };

  return { status, ...final };
};

export const generateRedirectLink = (id: number, token: string) => {
  return `${EVENTS_BASE}${id}?auth=${btoa(token)}`;
};

export const generateICS = (
  events: DownloadICS | DownloadICS[],
  user_tk: string,
) => {
  if (!Array.isArray(events)) {
    events = [events];
  }

  const A = "\r\n";

  let content = `BEGIN:VCALENDAR${A}`;
  content += `VERSION:2.0${A}`;
  content += `PRODID:-//EDUSOGNO//NONSGML v1.0//EN${A}`;

  events.forEach((e) => {
    const uid = e.id;
    const timestamp = dayjs().format("YYYYMMDDTHHmmss");
    const startDate = dayjs(`${e.data} ${e.ora}`);

    const formatData = startDate.format("DD MMMM YYYY, HH:mm");
    const endMoment = e.ora_fine
      ? dayjs(`${e.data} ${e.ora_fine}`)
      : dayjs(startDate).add(1, "hours");
    const durationMinutes = endMoment.diff(startDate, "minutes");
    const hours = Math.floor(durationMinutes / 60);
    const minutes2 = durationMinutes % 60;
    const durationStr =
      hours > 0
        ? `${hours} or${hours > 1 ? "e" : "a"} ${minutes2} minuti`
        : `${minutes2} minuti`;
    const dtstart = startDate.format("YYYYMMDDTHHmmss");
    const dtend = endMoment.format("YYYYMMDDTHHmmss");

    const meet = generateRedirectLink(e.id, user_tk);
    const description =
      "\n\nLesson: " +
      e.titolo +
      "\n\nLocation: " +
      meet +
      "\n\nDate and time: " +
      formatData +
      "\n\nDuration: " +
      durationStr;

    content += `BEGIN:VEVENT${A}`;
    content += `UID:${uid}@YourAppName${A}`;
    content += `DTSTAMP:${timestamp}${A}`;
    content += `DTSTART:${dtstart}${A}`;
    content += `DTEND:${dtend}${A}`;
    content += `SUMMARY:Edusogno | ${e.titolo}${A}`;
    content += `URL:${meet}${A}`;
    content += `LOCATION:${meet}${A}`;
    content += `DESCRIPTION:${description
      .replace(/\n/g, "\\n")
      .replace(/,/g, "\\,")}${A}`;
    content += `END:VEVENT${A}`;
  });

  content += `END:VCALENDAR`;

  return content;
};

export const downloadICS = (
  events: DownloadICS | DownloadICS[],
  user_tk: string,
) => {
  if (!Array.isArray(events)) {
    events = [events];
  }

  const filteredEvents = events.filter((e) =>
    dayjs(e.data).isSameOrAfter(dayjs(), "day"),
  );

  const ics = generateICS(filteredEvents, user_tk);
  const blob = new Blob([ics], { type: "text/calendar" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = "edusogno_calendar.ics";
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

/**
 * It returns an array of 14 strings, each string representing a time in the format of "HH:mm" starting
 * from 8:00am
 * @returns An array of strings, each string representing a time in the format HH:mm.
 */
export const getTime = () => {
  const time = dayjs().set("hour", 8).set("minute", 0).set("second", 0);
  return Array(15)
    .fill(0)
    .map((_, idx) => time.add(idx, "hour").format("HH:mm"));
};

/**
 * It takes an array of objects, and returns true if the array contains at least 3 unique values of a
 * specific property
 * @returns A function that takes in an array of SelectedSlot objects and returns a boolean.
 * @param events
 * @param startWeekDate
 * @param endWeekDate
 */
const columnModified = (events: EventsItem[]): SlotType[] => {
  return events.map(
    ({
      data,
      ora,
      ora_fine,
      nome_visualizzato,
      descrizione,
      ...rest
    }: EventsItem): SlotType => {
      // const startDate = dayjs(
      //   (data
      //     ? dayjs
      //         .tz(`${data}:${ora}`, "YYYY-MM-DD:hh:mm", "Europe/Rome")
      //         .local()
      //     : dayjs()
      //   ).format(INTERNAL_DATE_FORMAT_WITH_TIME),
      //   INTERNAL_DATE_FORMAT_WITH_TIME,
      // );
      const eventDate = moment.isMoment(data) ? data : moment(data);
      const startDate = moment(
        eventDate.format("YYYY-MM-DD") + " " + ora,
        "YYYY-MM-DD HH:mm",
      );

      // const endTime = dayjs(
      //   (ora_fine == null
      //     ? dayjs
      //         .tz(`${data}:${ora}`, "YYYY-MM-DD:hh:mm", "Europe/Rome")
      //         .add(1, "hour")
      //         .local()
      //     : dayjs
      //         .tz(`${data}:${ora_fine}`, "YYYY-MM-DD:hh:mm", "Europe/Rome")
      //         .local()
      //   ).format(INTERNAL_DATE_FORMAT_WITH_TIME),
      //   INTERNAL_DATE_FORMAT_WITH_TIME,
      // );
      const endTime =
      ora_fine == null
        ? moment(
            eventDate.format("YYYY-MM-DD") + " " + ora,
            "YYYY-MM-DD HH:mm",
          ).add({ hours: 1 })
        : moment(
            eventDate.format("YYYY-MM-DD") + " " + ora_fine,
            "YYYY-MM-DD HH:mm",
          );


      const duration = endTime.diff(startDate, "minutes");
      let colSpan: number = Math.ceil(duration / 60);
      if (endTime.minute() > 20 && colSpan > 0) colSpan++;

      const rowStart: number = Math.max(
        1,
        Math.ceil((startDate.minute() / (60 * colSpan)) * 21) + 1,
      );
      const rowEnd: number = Math.min(
        21,
        Math.floor(((startDate.minute() + duration) / (60 * colSpan)) * 21),
      );
      const title: string = nome_visualizzato || descrizione;

      return {
        data,
        ora_fine:
          ora_fine ?? startDate.add(1, "hour").format("YYYY-MM-DD hh:mm"),
        ora,
        duration: endTime.diff(startDate, "minutes"),
        startDate: startDate.subtract(20, "minutes"),
        endDate: endTime,
        descrizione,
        colSpan,
        title,
        nome_visualizzato,
        slotStyle: `row-start-${rowStart} row-end-${rowEnd}`,
        ...rest,
      };
    },
  );
};

// const eventsInPeriod = (events: EventsItem[], period: string): EventsItem[] => {
//   return events?.filter((r: EventsItem) => {
//     const d: dayjs.Dayjs = dayjs.tz(`${r?.data} ${r.ora}`, "Europe/Rome");
//     return dayjs(d.format("HH:mm"), "HH:mm").isSame(
//       dayjs(period, "HH:mm"),
//       "hour",
//     );
//   });
// };


const eventsInPeriod = (events: EventsItem[], period: string): EventsItem[] => {
  return events?.filter((r: EventsItem) => {
    const d = moment.tz(`${r?.data} ${r.ora}`, "YYYY-MM-DD HH:mm", "Europe/Rome");
    return moment(d.format("HH:mm"), "HH:mm").isSame(
      moment(period, "HH:mm"),
      'hour'
    );
  });
};

// Utility functions

const groupEventsByDate = (
  events: EventsItem[],
): Record<string, EventsItem[]> => {
  return events.reduce(
    (acc: Record<string, EventsItem[]>, event: EventsItem) => {
      const dateKey: string = event?.convertedStartTime.format("YYYY-MM-DD");
      // const dateKey: string = event?.data;
      acc[dateKey] = acc[dateKey] || [];
      acc[dateKey].push({
        ...event,
        ora: event.convertedStartTime.format("HH:mm"),
        ora_fine: event.convertedEndTime.format("HH:mm"),
        data: dateKey 
      });
      return acc;
    },
    {},
  );
};

// Utility function to convert time from Rome to selected timezone
const convertTimeToTimezone = (date: string, time: string, tz: string) => {
  return moment.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm", "Europe/Rome")
    .tz(tz);
};

const generateSlots = (week: string[], events: EventsItem[], timezon: string): SlotType[][] => {
  const modifiedArr: SlotType[][] = [];
  const times: string[] = getTime(); // Not sure about the return type of getTime()

  // Convert all events to the selected timezone first
  const convertedEvents = events.map(event => {
    const convertedStartTime = convertTimeToTimezone(event.data, event.ora, timezon);
    const convertedEndTime = event.ora_fine 
      ? convertTimeToTimezone(event.data, event.ora_fine, timezon)
      : moment(convertedStartTime).add(1, 'hour');

    return {
      ...event,
      convertedStartTime,
      convertedEndTime
    };
  });

  const eventsByDate: Record<string, EventsItem[]> = groupEventsByDate(convertedEvents);

  for (let i = 0; i < week.length; i++) {
    const currentDate: dayjs.Dayjs = dayjs(week[i], INTERNAL_DATE_FORMAT);
    const eventsOnCurrentDate: EventsItem[] =
      eventsByDate[currentDate.format("YYYY-MM-DD")] || [];

    const tempArr: SlotType[] = [];
    let columnLength: number = 1;
    for (let j = 0; j < times.length; j++) {
      if (columnLength >= times.length) break;
      if (columnLength > j + 1) continue;
      const results: EventsItem[] = eventsInPeriod(
        eventsOnCurrentDate,
        times[j],
      );
      const eventsModified = columnModified(results);
      const columnStyle: string = "grid-rows-[repeat(20,auto)]";
      let colSpan: number = 1;
      eventsModified.forEach((v: SlotType) => {
        if (v?.colSpan ?? 0 > colSpan) colSpan = v.colSpan as number;
      });

      tempArr.push({
        rowStyle: `row-start-${columnLength} row-end-${columnLength + colSpan}`,
        childEvents: eventsModified as unknown as ModifiedEventsItem[],
        columnStyle: columnStyle,
      });

      columnLength += colSpan;
    }
    modifiedArr.push(tempArr);
  }

  return modifiedArr;
};

export const generateMonthlySlots = (events: EventsItem[], tz: string) => {
  if(events?.length === 0 ) return;
  
  const convertedEvents = events?.map(event => {
    // First convert the event time from Rome to the target timezone
    const startMoment = moment.tz(`${event.data} ${event.ora}`, "YYYY-MM-DD HH:mm", "Europe/Rome")
      .tz(tz);
    
    const endMoment = event.ora_fine
      ? moment.tz(`${event.data} ${event.ora_fine}`, "YYYY-MM-DD HH:mm", "Europe/Rome")
          .tz(tz)
      : moment(startMoment).add(1, "hour");

    const duration = endMoment.diff(startMoment, "minutes");
    const isPast = endMoment.isBefore(moment().tz(tz));

    return {
      ...event,
      startDate: startMoment,
      endTime: endMoment.format("HH:mm"),
      duration,
      isPast,
      ora: startMoment.format("HH:mm"),
      ora_fine: endMoment.format("HH:mm"),
      data: startMoment.format("YYYY-MM-DD"),
    };
  });  
  return convertedEvents;
}

export { columnModified, eventsInPeriod, generateSlots };

export const getEndTime = (event: EventsItem) => {
  const { data, ora, ora_fine: oraFine } = event;
  const endTime =
    oraFine == null
      ? dayjs
          .tz(data + " " + ora, "Europe/Rome")
          .add(1, "hour")
          .local()
      : dayjs.tz(data + " " + oraFine, "Europe/Rome").local();
  return endTime.format("HH:mm");
};

export const getStartTime = (event: EventsItem) => {
  const { data, ora } = event;
  const startDate = dayjs.tz(data + " " + ora, "Europe/Rome").local();
  return startDate.format("HH:mm");
};

export const getTitle = (event: EventsItem) => {
  const { titolo, nome_visualizzato: nv } = event;
  return nv ? nv : titolo;
  // return title.replace(/\s\w+$/, "");
};

export const getDateBetween = (
  startDate: Dayjs,
  endDate: Dayjs,
): Array<unknown> => {
  const calendar: string[] = [];
  const start = startDate.startOf("month").startOf("isoWeek");
  const end = endDate.endOf("month");

  const delta = end.diff(start, "day");

  for (let i = 0; i <= delta; i++) {
    const day = start.add(i, "day").format(INTERNAL_DATE_FORMAT);
    calendar.push(day);
  }

  return calendar;
};

//Mobile UI Utility functions
export const updateCalenderAndHeader = (attribute: string | null) => {
  const miniCalendar = document.querySelector(`#miniCalendar`);
  const monthHeader = document.querySelector(`#monthHeader`);
  if (attribute !== null) {
    miniCalendar?.setAttribute("data-month", attribute);
    if (monthHeader !== null)
      monthHeader.innerHTML = dayjs(attribute).format("MMMM YYYY");
  }
};

export const scrollToday = () => {
  const todayElement = document.querySelector(`[data-todaytarget="true"]`);
  const todayButton = document.querySelector(`#todayButton`) || null;

  if (todayElement && todayButton) {
    const attribute = todayElement.getAttribute("data-dateitem");

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    todayElement.parentNode.scrollTop = todayElement.offsetTop - 100;

    // NOTE: scrollIntoView causes the page to scroll to the top of the element, which is not what we want.
    // todayElement.scrollIntoView({
    //   behavior: "smooth",
    //   block: "nearest",
    //   inline: "start",
    // });
    todayButton.setAttribute("data-alt", "true");
    todayButton.setAttribute("disabled", "true");

    updateCalenderAndHeader(attribute);
  }
};

export const getDateWeek = (date: dayjs.Dayjs) => {
  const calendar: string[][] = [];
  const startDate = date.startOf("month").startOf("isoWeek");
  const endDate = date.endOf("month");
  let day = startDate.subtract(1, "day");
  while (day.isBefore(endDate, "day")) {
    calendar.push(
      Array(7)
        .fill(0)
        // eslint-disable-next-line @typescript-eslint/no-loop-func
        .map(() => {
          day = day.add(1, "day");
          return day.format(INTERNAL_DATE_FORMAT);
        }),
    );
  }
  // return unique values
  return calendar.filter((item, index) => calendar.indexOf(item) === index);
};
